import { useEffect, useMemo } from "react";
import { ResponsiveContainer, PieChart, Pie, Sector, Cell } from "recharts";
import { motion, useAnimationControls } from "framer-motion";

const WIN_COLOR = "#40BF6A";
const LOSS_COLOR = "#DF2040";
const symbolPalette = [
  "#4C8F4C",
  "#CFFF31",
  "#5A6FB3",
  "#59A14F",
  "#88C057",
  "#7EA6E0",
  "#3F7F3F",
  "#A6CE39",
  "#6A7FCF",
  "#97D077",
  "#4B6BB3",
  "#6FCF97",
];

function calloutActive({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  name,
  value,
  percent,
}) {
  const RAD = Math.PI / 180;
  const sin = Math.sin(-RAD * (midAngle ?? 0));
  const cos = Math.cos(-RAD * (midAngle ?? 0));
  const sx = (cx ?? 0) + ((outerRadius ?? 0) + 12) * cos;
  const sy = (cy ?? 0) + ((outerRadius ?? 0) + 12) * sin;
  const mx = (cx ?? 0) + ((outerRadius ?? 0) + 36) * cos;
  const my = (cy ?? 0) + ((outerRadius ?? 0) + 36) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 28;
  const ey = my;
  const ta = cos >= 0 ? "start" : "end";
  const v = Number.isFinite(value) ? value : 0;
  const pct = ((percent ?? 0) * 100).toFixed(1) + "%";
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 2}
        outerRadius={(outerRadius ?? 0) + 8}
        fill={fill}
        opacity={0.35}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        strokeWidth={3}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={4} fill={fill} />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 14}
        y={ey}
        textAnchor={ta}
        fill="#fff"
        style={{ fontWeight: 700, fontSize: 24 }}
      >
        {name}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 14}
        y={ey}
        dy={26}
        textAnchor={ta}
        fill="#E5E7EB"
        style={{ fontWeight: 700, fontSize: 24 }}
      >
        {new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v)}{" "}
        • {pct}
      </text>
    </g>
  );
}

export default function TradeAnalysisComponent({ data }) {
  const positions = Array.isArray(data?.positions) ? data.positions : [];
  const metrics = data?.metrics || {};

  const symbolDist = useMemo(() => {
    const m = new Map();
    for (const p of positions) {
      const k = p?.symbol || "—";
      m.set(k, (m.get(k) || 0) + 1);
    }
    const arr = Array.from(m.entries()).map(([name, value], i) => ({
      name,
      value,
      fill: symbolPalette[i % symbolPalette.length],
    }));
    return arr.length ? arr : [{ name: "—", value: 1, fill: symbolPalette[0] }];
  }, [positions]);

  const winLoss = useMemo(() => {
    let wins = Number(metrics?.winning_trades) || 0;
    let losses = Number(metrics?.losing_trades) || 0;
    if (wins + losses === 0 && positions.length) {
      for (const p of positions) {
        const net = Number(p?.net_profit ?? p?.total_profit ?? 0);
        if (net > 0) wins += 1;
        else if (net < 0) losses += 1;
      }
    }
    if (wins + losses === 0) wins = 1;
    return [
      { name: "برد", value: wins, fill: WIN_COLOR },
      { name: "باخت", value: losses, fill: LOSS_COLOR },
    ];
  }, [metrics, positions]);

  const innerRadius = 52;
  const outerRadius = 112;

  const svgCtrl = useAnimationControls();
  const edgeLeftCtrl = useAnimationControls();
  const edgeRightCtrl = useAnimationControls();

  useEffect(() => {
    (async () => {
      await svgCtrl.start({
        strokeDashoffset: 0,
        opacity: 1,
        transition: { duration: 0.5, ease: "easeInOut" },
      });
      await svgCtrl.start("horiz");
      await Promise.all([
        edgeLeftCtrl.start("edge"),
        edgeRightCtrl.start("edge"),
      ]);
    })();
  }, []);

  const TOTAL_W = 1200;
  const GAP = 248;
  const COL_W = (TOTAL_W - GAP) / 2;
  const CENTER_X = TOTAL_W / 2;
  const LEFT_CX = COL_W / 2;
  const RIGHT_CX = COL_W + GAP + COL_W / 2;

  const VB_H = 160;
  const Y_SPLIT = 36;
  const EDGE_DROP = 18;
  const DOT_R = 3;

  return (
    <div className="w-full flex items-center justify-center flex-col">
      <div className="w-[1000px] flex flex-col items-center justify-center overflow-hidden rounded-md">
        <span className="text-[26px] text-[#FFFFFF] font-bold">
          تحلیل معاملات
        </span>
        <div className="relative mt-4 mb-[142px] h-5 w-[40rem]">
          <div className="absolute inset-x-20 top-0 h-[3px] w-3/4 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md" />
          <div className="absolute inset-x-20 top-0 h-px w-3/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_8px_#ffffff]" />
          <div className="absolute inset-x-60 top-0 h-[6px] w-1/4 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-md" />
          <div className="absolute inset-x-60 top-0 h-px w-1/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_10px_#ffffff]" />
          <div className="absolute inset-x-44 top-0 h-[2px] w-2/4 bg-gradient-to-r from-transparent via-sky-100/60 to-transparent blur-[2px]" />
          <div className="pointer-events-none absolute inset-0 h-full w-full [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]" />
        </div>
      </div>

      <div className="relative w-[1200px] -mt-[142px] mb-[0px] h-[160px] pointer-events-none">
        <svg
          viewBox={`0 0 ${TOTAL_W} ${VB_H}`}
          className="absolute left-1/2 -translate-x-1/2 w-[1200px] h-[160px] overflow-visible"
          preserveAspectRatio="none"
        >
          <defs>
            <clipPath id="ta-clip-right">
              <motion.rect
                initial={{ x: CENTER_X, width: 0, y: 0, height: VB_H }}
                animate={svgCtrl}
                variants={{
                  horiz: {
                    x: CENTER_X,
                    width: TOTAL_W - CENTER_X,
                    transition: { duration: 0.8, ease: "easeInOut" },
                  },
                }}
              />
            </clipPath>
            <clipPath id="ta-clip-left">
              <motion.rect
                initial={{ x: CENTER_X, width: 0, y: 0, height: VB_H }}
                animate={svgCtrl}
                variants={{
                  horiz: {
                    x: 0,
                    width: CENTER_X,
                    transition: { duration: 0.8, ease: "easeInOut" },
                  },
                }}
              />
            </clipPath>
          </defs>

          <motion.path
            d={`M ${CENTER_X} 0 L ${CENTER_X} ${Y_SPLIT}`}
            stroke="#FFFFFF"
            strokeWidth={0.8}
            strokeDasharray="2 2"
            initial={{ strokeDashoffset: Y_SPLIT, opacity: 0 }}
            animate={svgCtrl}
          />

          <path
            d={`M ${CENTER_X} ${Y_SPLIT} L ${RIGHT_CX} ${Y_SPLIT}`}
            stroke="#FFFFFF"
            strokeWidth={0.8}
            strokeDasharray="8 6"
            clipPath="url(#ta-clip-right)"
          />
          <path
            d={`M ${CENTER_X} ${Y_SPLIT} L ${LEFT_CX} ${Y_SPLIT}`}
            stroke="#FFFFFF"
            strokeWidth={0.8}
            strokeDasharray="8 6"
            clipPath="url(#ta-clip-left)"
          />

          <motion.path
            d={`M ${LEFT_CX} ${Y_SPLIT} L ${LEFT_CX} ${Y_SPLIT + EDGE_DROP}`}
            stroke="#FFFFFF"
            strokeWidth={0.8}
            strokeDasharray="2 2"
            initial={{ strokeDashoffset: EDGE_DROP, opacity: 0 }}
            animate={edgeLeftCtrl}
            variants={{
              edge: {
                strokeDashoffset: 0,
                opacity: 1,
                transition: { duration: 0.4, ease: "easeInOut" },
              },
            }}
          />
          <motion.circle
            cx={LEFT_CX}
            cy={Y_SPLIT + EDGE_DROP + (DOT_R + 0.3)}
            r={DOT_R}
            fill="#FFFFFF"
            initial={{ scale: 0, opacity: 0 }}
            animate={edgeLeftCtrl}
            variants={{
              edge: {
                scale: 1,
                opacity: 1,
                transition: { delay: 0.3, duration: 0.25, ease: "backOut" },
              },
            }}
          />

          <motion.path
            d={`M ${RIGHT_CX} ${Y_SPLIT} L ${RIGHT_CX} ${Y_SPLIT + EDGE_DROP}`}
            stroke="#FFFFFF"
            strokeWidth={0.8}
            strokeDasharray="2 2"
            initial={{ strokeDashoffset: EDGE_DROP, opacity: 0 }}
            animate={edgeRightCtrl}
            variants={{
              edge: {
                strokeDashoffset: 0,
                opacity: 1,
                transition: { duration: 0.4, ease: "easeInOut" },
              },
            }}
          />
          <motion.circle
            cx={RIGHT_CX}
            cy={Y_SPLIT + EDGE_DROP + (DOT_R + 0.3)}
            r={DOT_R}
            fill="#FFFFFF"
            initial={{ scale: 0, opacity: 0 }}
            animate={edgeRightCtrl}
            variants={{
              edge: {
                scale: 1,
                opacity: 1,
                transition: { delay: 0.3, duration: 0.25, ease: "backOut" },
              },
            }}
          />
        </svg>
      </div>

      <div className="w-[1200px] grid grid-cols-1 md:grid-cols-2 gap-[248px]">
        <div className="w-full  flex items-center justify-center flex-col gap-[74px]">
          <span className="text-[#FFFFFF] text-[26px] font-bold">
            توزیع نماد معاملاتی
          </span>
          <div className="h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={symbolDist}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  dataKey="value"
                  labelLine={false}
                  label={calloutActive}
                  isAnimationActive
                >
                  {symbolDist.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="w-full  flex items-center justify-center flex-col gap-[74px]">
          <span className="text-[#FFFFFF] text-[26px] font-bold">درصد برد</span>
          <div className="h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={winLoss}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  dataKey="value"
                  labelLine={false}
                  label={calloutActive}
                  isAnimationActive
                >
                  {winLoss.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
